import {
  ACTION_SMS_SUBSCRIBE_CONFIRMATION_MODAL_CLOSE,
  ACTION_SMS_SUBSCRIBE_CONFIRMATION_MODAL_OPEN,
  ACTION_SMS_SUBSCRIBE_CONFIRMATION_MODAL_SUBSCRIBE,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeading,
  Typography,
} from 'omakase';
import { Box } from '@material-ui/core';
import { FC, useEffect } from 'react';
import { IconBubbleExclamation } from '@tock/icons/dist';
import { PageType } from 'utils/analytics/eventTypesConstants';
import { SmsAlertStopped } from 'components/sms/SmsAlertStopped';
import { core } from '@tock/coretypes';
import { privacyPolicyLink, termsLink } from 'utils/legal/termsUtils';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';

export const TOCK_DIRECT_BRAND_PHONE_NUMBER = '+18557298625';

interface Props {
  onClose(optStatusType: core.SmsPhone.StatusType): void;
  isOpen: boolean;
  curPatronHardUnsubscribed: boolean;
  pageType: PageType;
}

export const SmsSubscribeConfirmationModal: FC<Props> = ({
  onClose,
  isOpen,
  curPatronHardUnsubscribed,
  pageType,
}) => {
  const titleText = 'Enable text alerts from Tock';
  const amplitudeSendEvent = useAmplitudeSendEvent();

  useEffect(() => {
    if (isOpen) {
      amplitudeSendEvent(ACTION_SMS_SUBSCRIBE_CONFIRMATION_MODAL_OPEN, { pageType });
    }
  }, [isOpen, amplitudeSendEvent, pageType]);

  /**
   * Closing the modal is a neutral choice, aka NOT_SPECIFIED.
   */
  const handleClose = () => {
    amplitudeSendEvent(ACTION_SMS_SUBSCRIBE_CONFIRMATION_MODAL_CLOSE, { pageType });
    return onClose('NOT_SPECIFIED');
  };

  /**
   * The patron chooses to OPT_IN to SMS
   */
  const handleConfirm = () => {
    amplitudeSendEvent(ACTION_SMS_SUBSCRIBE_CONFIRMATION_MODAL_SUBSCRIBE, { pageType });
    return onClose('OPT_IN');
  };

  return (
    <Dialog
      fullWidth
      aria-label={titleText}
      variant="sheet"
      open={isOpen}
      onClose={handleClose}
      data-testid="sms-confirmation-dialog"
    >
      <DialogHeading
        id="sms-confirmation-dialog-title"
        onClose={handleClose}
        data-testid="sms-confirmation-modal-title"
      >
        {titleText}
      </DialogHeading>
      <DialogContent
        id="sms-confirmation-dialog-content"
        data-testid="sms-confirmation-dialog-content"
      >
        {curPatronHardUnsubscribed ? (
          <SmsAlertStopped severity="error" />
        ) : (
          <Alert
            severity="basic"
            heading="Stay in the know about your table"
            icon={<IconBubbleExclamation />}
          >
            Receive text confirmation and updates for this and future bookings.
          </Alert>
        )}
        <Box style={{ marginTop: '16px' }}>
          <Typography variant="captionStrong" color="fgSubtle">
            SMS terms and conditions{' '}
          </Typography>
          <Typography variant="caption" color="fgSubtle">
            By providing your mobile number, you agree to the Tock {termsLink('', true)}
            and {privacyPolicyLink('', true)}
            and your mobile number may be used to send you text messages and call you, through the
            use of an auto dialer, artificial, and/or prerecorded call or text, to service your Tock
            account including to confirm and manage reservations. Message and data rates may apply.
            Message frequency varies. You may opt out of receiving text messages at any time by
            replying STOP. Text HELP for help.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        {curPatronHardUnsubscribed ? (
          <Button
            data-testid="sms-continue-button"
            size="large"
            variant="basic"
            onClick={handleClose}
          >
            Continue
          </Button>
        ) : (
          <>
            <Button
              data-testid="sms-skip-button"
              size="large"
              variant="basic"
              onClick={handleClose}
            >
              Skip
            </Button>
            <Button
              data-testid="sms-agree-button"
              size="large"
              variant="primary"
              onClick={handleConfirm}
            >
              Agree and Continue
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
